/**
 * DadHeartJourney · public one-tap Heart Flow session for Dad.
 *
 * Mirror of MumSpineJourney — same three-step technophobe-friendly
 * shell (Begin → Family Circle → Immersion → Rest) — configured for
 * the Heart Flow healing (post heart-valve recovery).
 */
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import HeartImmersion from "../HealingSupport/HeartImmersion";
import FamilyCircleTile from "./FamilyCircleTile";

const API = process.env.REACT_APP_BACKEND_URL;

// Family circle for Dad's Heart Flow page — 4 voices, each reading
// the SAME heart-flow script (Michael's recording is the master; the
// others are ElevenLabs voice clones rendered from their existing
// family-voice samples). Millie will be added once we have a longer
// clean sample from her (target 60s+ for a natural clone).
const FAMILY_VOICES = [
  {
    id: "daisy-heart",
    speaker_name: "Daisy",
    relation: "Granddaughter",
    accent: "#F6C89C",
    audioPath: "/api/audio/healing/heart_flow_daisy.mp3",
  },
  {
    id: "charlie-heart",
    speaker_name: "Charlie",
    relation: "Grandson",
    accent: "#C79B7C",
    audioPath: "/api/audio/healing/heart_flow_charlie.mp3",
  },
  {
    id: "mia-heart",
    speaker_name: "Mia",
    relation: "Granddaughter",
    accent: "#E9BFA8",
    audioPath: "/api/audio/healing/heart_flow_mia.mp3",
  },
  {
    id: "millie-heart",
    speaker_name: "Millie",
    relation: "Granddaughter",
    accent: "#F0CDA8",
    audioPath: "/api/audio/healing/heart_flow_millie.mp3",
  },
  {
    id: "jane-heart",
    speaker_name: "Jane",
    relation: "Daughter",
    accent: "#E6B489",
    audioPath: "/api/audio/healing/heart_flow_jane.mp3",
  },
  {
    id: "michael-heart",
    speaker_name: "Michael",
    relation: "Son",
    accent: "#D9AB6E",
    audioPath: "/api/audio/healing/heart_flow_michael.mp3",
  },
];

export default function DadHeartJourney() {
  const [phase, setPhase] = useState("idle");
  const [selectedVoice, setSelectedVoice] = useState(null);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    document.body.dataset.immersive = "true";
    const prevTitle = document.title;
    document.title = "Heart Flow · Dad";
    const added = [];
    const setMeta = (attrs) => {
      const el = document.createElement("meta");
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      document.head.appendChild(el);
      added.push(el);
    };
    const setLink = (attrs) => {
      const el = document.createElement("link");
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      document.head.appendChild(el);
      added.push(el);
    };
    setMeta({ name: "apple-mobile-web-app-capable",          content: "yes" });
    setMeta({ name: "apple-mobile-web-app-status-bar-style", content: "black-translucent" });
    setMeta({ name: "apple-mobile-web-app-title",            content: "Heart" });
    setMeta({ name: "theme-color",                           content: "#180b10" });
    setMeta({ name: "viewport",                              content: "width=device-width, initial-scale=1, viewport-fit=cover" });
    setLink({ rel: "apple-touch-icon",                       href: "/apple-touch-icon.png" });
    return () => {
      delete document.body.dataset.immersive;
      document.title = prevTitle;
      added.forEach((el) => { try { el.remove(); } catch (_) { /* noop */ } });
    };
  }, []);

  return (
    <div
      data-testid="dad-heart-journey"
      className="fixed inset-0 overflow-hidden"
      style={{ background: "radial-gradient(circle at 50% 45%, #1a0d12 0%, #0a060a 55%, #050206 100%)" }}
    >
      <AnimatePresence mode="wait">
        {phase === "idle" && (
          <IdleScreen key="idle" onStart={() => setPhase("circle")} />
        )}
        {phase === "circle" && (
          <FamilyCircleScreen
            key="circle"
            voices={FAMILY_VOICES}
            onPick={(v) => {
              // Log the pick — best-effort, silent on failure
              try {
                fetch(`${API}/api/family-journey/pick`, {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    recipient: "grandad",
                    page: "heart",
                    voice_id: v.id,
                    speaker_name: v.speaker_name,
                  }),
                }).catch(() => {});
              } catch (_) { /* noop */ }
              setSelectedVoice(v); setPhase("session");
            }}
          />
        )}
        {phase === "session" && selectedVoice && (
          <motion.div
            key="session"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 1.2 }}
            className="fixed inset-0"
          >
            <HeartImmersion
              voiceOverride={{
                url: `${API}${selectedVoice.audioPath}`,
                speaker_name: selectedVoice.speaker_name,
              }}
              autoStart
              keepsakeMeta={{
                recipient:    "grandad",
                page:         "heart",
                voice_id:     selectedVoice.id,
                speaker_name: selectedVoice.speaker_name,
              }}
              onComplete={() => setPhase("complete")}
            />
          </motion.div>
        )}
        {phase === "complete" && (
          <CompleteScreen key="complete" onAgain={() => setPhase("circle")} />
        )}
      </AnimatePresence>
    </div>
  );
}

function IdleScreen({ onStart }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 1.0, ease: [0.22, 1, 0.36, 1] }}
      className="relative z-10 flex flex-col items-center justify-center gap-10 px-8 text-center min-h-screen"
    >
      <motion.div
        className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(circle at 50% 55%, rgba(215, 100, 90, 0.15) 0%, transparent 62%)" }}
        animate={{ opacity: [0.7, 1, 0.7] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      />
      <div className="relative flex flex-col items-center gap-3">
        <div
          className="text-amber-100/80 tracking-[0.35em] uppercase text-[10px]"
          style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}
        >
          Reclaim · Heart Flow
        </div>
        <div
          className="text-amber-50 text-4xl sm:text-5xl leading-tight"
          style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}
        >
          Welcome home, <span className="italic text-rose-200/95">Dad</span>
        </div>
        <div
          className="text-white/50 text-base max-w-sm mx-auto italic mt-3 leading-relaxed"
          style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}
        >
          Put on headphones if you can.<br/>
          Lie back — three slow breaths first,<br/>
          then the voice you choose walks you in.
        </div>
      </div>
      <motion.button
        onClick={onStart}
        data-testid="dad-heart-begin"
        whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
        className="relative flex items-center justify-center rounded-full"
        style={{
          width: 240, height: 240,
          background: "radial-gradient(circle at 50% 45%, #f6d68a 0%, #d59f4f 45%, #8a5b26 100%)",
          boxShadow: "0 0 70px rgba(245, 200, 130, 0.55), 0 0 140px rgba(200, 130, 80, 0.35), inset 0 6px 26px rgba(255, 240, 200, 0.5), inset 0 -6px 26px rgba(120, 60, 30, 0.5)",
        }}
      >
        <motion.span
          className="absolute inset-0 rounded-full"
          style={{ border: "1px solid rgba(255, 224, 170, 0.55)" }}
          animate={{ scale: [1, 1.08, 1], opacity: [0.6, 0.15, 0.6] }}
          transition={{ duration: 3.4, repeat: Infinity, ease: "easeInOut" }}
        />
        <span
          className="text-[#3a1e08] text-3xl tracking-wide"
          style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 600, letterSpacing: "0.03em" }}
        >
          Begin
        </span>
      </motion.button>
      <div className="text-white/40 text-xs italic tracking-widest">
        A gentle heart flow · warmth returning
      </div>
    </motion.div>
  );
}

function FamilyCircleScreen({ voices, onPick }) {
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
      className="relative z-10 flex flex-col items-center gap-6 px-8 pt-14 pb-16 text-center h-full overflow-y-auto"
    >
      <div className="flex flex-col items-center gap-2 shrink-0">
        <div
          className="text-amber-100/80 tracking-[0.35em] uppercase text-[10px]"
          style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}
          data-testid="dad-heart-circle-title"
        >
          Choose your voice
        </div>
        <div
          className="text-amber-50 text-2xl sm:text-3xl italic"
          style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}
        >
          who would you like to walk with you?
        </div>
      </div>
      <div className="grid grid-cols-2 gap-5 sm:gap-8 mt-2 shrink-0">
        {voices.map((v, i) => (
          <FamilyCircleTile
            key={v.id}
            voice={v}
            index={i}
            previewSrc={`${API}${v.audioPath}`}
            testIdPrefix="dad-heart-voice"
            onPick={onPick}
          />
        ))}
      </div>
      <div className="mt-4 text-white/35 text-xs italic tracking-widest">
        Tap a face — it takes you straight in
      </div>
    </motion.div>
  );
}

function CompleteScreen({ onAgain }) {
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
      className="relative z-10 flex flex-col items-center justify-center gap-8 px-8 text-center min-h-screen"
    >
      <div className="flex flex-col items-center gap-3">
        <div
          className="text-amber-50 text-3xl sm:text-4xl italic leading-tight"
          style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}
        >
          Rest, <span className="text-rose-200/95">Dad</span>
        </div>
        <div
          className="text-white/50 text-base max-w-sm mx-auto italic mt-2"
          style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}
        >
          Your heart is held. The warmth stays with you.
        </div>
      </div>
      <motion.button
        onClick={onAgain}
        data-testid="dad-heart-again"
        whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
        className="text-amber-100/80 text-sm tracking-[0.25em] uppercase border-b border-amber-100/25 pb-1 hover:text-amber-100"
      >
        Again
      </motion.button>
    </motion.div>
  );
}
